import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { useNavigate } from "react-router";

import { Translate } from "@/app/application/Translate";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { useDeleteChecklist } from "@/features/procedures/api/checklist.mutations";
import { ChecklistQueryKeys } from "@/features/procedures/api/checklist.queries";
import { DeleteProcedureNode } from "@/features/procedures/api/procedure.mutations";
import { ProcedureDetails } from "@/features/procedures/model/procedure/ProcedureDetail";
import { ProcedureDetailNode } from "@/features/procedures/model/types/node";
import { DeleteProcedureNodeRequest } from "@/features/procedures/model/types/procedure";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";

import { ProcedureQueryKeys } from "@/service/ProcedureQueries";

const UNDO_DURATION_MS = 30000;
const STORAGE_KEY = "procedures.pending-destructive-delete.v1";

type PendingChecklistDelete = {
	type: "checklist";
	schemeId: number;
	procedureId: number;
	itemName: string;
	expiresAt: number;
	previousProcedureDetails: ProcedureDetails | undefined;
};

type PendingNodeDelete = {
	type: "node";
	schemeId: number;
	procedureId: number;
	itemName: string;
	expiresAt: number;
	previousProcedureDetails: ProcedureDetails | undefined;
	deleteRequest: DeleteProcedureNodeRequest;
};

type PendingProcedureDelete = {
	type: "procedure";
	procedureId: number;
	itemName: string;
	expiresAt: number;
};

type PendingCustomDelete = {
	type: "custom";
	id: string;
	message: string;
	expiresAt: number;
	onCommit: () => Promise<void> | void;
	onUndo?: () => void;
};

type PendingDelete =
	| PendingChecklistDelete
	| PendingNodeDelete
	| PendingProcedureDelete
	| PendingCustomDelete;
type PersistedPendingDelete =
	| Omit<PendingChecklistDelete, "previousProcedureDetails">
	| Omit<PendingNodeDelete, "previousProcedureDetails">
	| PendingProcedureDelete;
type QueryCacheEventShape = { query?: { queryKey?: unknown } };

type ContextValue = {
	showDeleteChecklistUndo: (
		checklistId: number,
		procedureId: number,
		checklistName: string,
	) => void;
	showDeleteNodeUndo: (
		schemeId: number,
		procedureId: number,
		nodeName: string,
		deleteRequest: DeleteProcedureNodeRequest,
		options?: { onClose?: () => void },
	) => void;
	showDeleteProcedureUndo: (procedureId: number, procedureName: string) => void;
	showDeferredUndo: (options: {
		message: string;
		onCommit: () => Promise<void> | void;
		onUndo?: () => void;
		durationMs?: number;
	}) => void;
};

const DestructiveActionUndoContext = createContext<ContextValue | null>(null);
let hasWarnedMissingUndoProvider = false;

const savePendingDelete = (pending: PersistedPendingDelete) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
	} catch {
		// Non-blocking persistence
	}
};

const clearPendingDelete = () => {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Non-blocking cleanup
	}
};

const parsePendingDelete = (): PersistedPendingDelete | null => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as PersistedPendingDelete | null;
		if (!parsed) return null;
		if (!parsed.type || typeof parsed.expiresAt !== "number") {
			return null;
		}
		if (parsed.type === "procedure") {
			if (typeof parsed.procedureId !== "number") return null;
			return parsed;
		}
		if (
			typeof parsed.schemeId !== "number" ||
			typeof parsed.procedureId !== "number"
		) {
			return null;
		}
		if (parsed.type === "node" && !("deleteRequest" in parsed)) return null;
		return parsed;
	} catch {
		return null;
	}
};

/** Recursively remove the node (or nested child) matching SchemeId from the tree */
function filterOutNode(
	nodes: ProcedureDetailNode[],
	schemeId: number,
): ProcedureDetailNode[] {
	return nodes
		.filter((node) => node.SchemeId !== schemeId)
		.map((node) => {
			if (node.Children?.length) {
				return {
					...node,
					Children: filterOutNode(node.Children, schemeId),
				};
			}
			return node;
		});
}

function hasNode(nodes: ProcedureDetailNode[], schemeId: number): boolean {
	for (const node of nodes) {
		if (node.SchemeId === schemeId) return true;
		if (node.Children?.length && hasNode(node.Children, schemeId)) return true;
	}
	return false;
}

function getRecordId(item: unknown): number | undefined {
	if (!item || typeof item !== "object") return undefined;
	const maybeId = item as { Id?: unknown; ProcedureId?: unknown };
	if (typeof maybeId.Id === "number") return maybeId.Id;
	if (typeof maybeId.ProcedureId === "number") return maybeId.ProcedureId;
	return undefined;
}

function containsProcedureInCollection(
	data: unknown,
	procedureId: number,
): boolean {
	if (Array.isArray(data)) {
		return data.some((item) => getRecordId(item) === procedureId);
	}
	if (!data || typeof data !== "object") return false;
	const maybePaged = data as { Items?: unknown };
	if (!Array.isArray(maybePaged.Items)) return false;
	return maybePaged.Items.some((item) => getRecordId(item) === procedureId);
}

function removeProcedureFromCollection<T>(data: T, procedureId: number): T {
	if (Array.isArray(data)) {
		return data.filter((item) => getRecordId(item) !== procedureId) as T;
	}
	if (!data || typeof data !== "object") return data;
	const maybePaged = data as { Items?: unknown[] };
	if (!Array.isArray(maybePaged.Items)) return data;
	return {
		...(data as object),
		Items: maybePaged.Items.filter((item) => getRecordId(item) !== procedureId),
	} as T;
}

function applyOptimisticUpdate(
	queryClient: ReturnType<typeof useQueryClient>,
	procedureId: number,
	schemeId: number,
	previousProcedureDetails: ProcedureDetails | undefined,
) {
	if (!previousProcedureDetails?.ProcedureDetailNodes) return;

	const queryKey = [ProcedureQueryKeys.procedureDetails, procedureId] as const;
	const updated = {
		...previousProcedureDetails,
		ProcedureDetailNodes: filterOutNode(
			previousProcedureDetails.ProcedureDetailNodes,
			schemeId,
		),
	};
	void queryClient.cancelQueries({ queryKey });
	queryClient.setQueryData(queryKey, updated);
}

function applyOptimisticProcedureRemoval(
	queryClient: ReturnType<typeof useQueryClient>,
	procedureId: number,
) {
	const listQueries = queryClient.getQueriesData({
		queryKey: [ProcedureQueryKeys.procedures],
	});
	for (const [queryKey, current] of listQueries) {
		if (!containsProcedureInCollection(current, procedureId)) continue;
		queryClient.setQueryData(
			queryKey,
			removeProcedureFromCollection(current, procedureId),
		);
	}
}

export function DestructiveActionUndoProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [pending, setPending] = useState<PendingDelete | null>(null);
	const pendingRef = useRef<PendingDelete | null>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const queryClient = useQueryClient();
	const deleteChecklist = useDeleteChecklist();
	const deleteProcedureNode = DeleteProcedureNode();
	const navigate = useNavigate();
	pendingRef.current = pending;

	const clearTimer = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const persistPending = useCallback((value: PendingDelete) => {
		if (value.type === "custom") return;
		if (value.type === "procedure") {
			savePendingDelete({
				type: "procedure",
				procedureId: value.procedureId,
				itemName: value.itemName,
				expiresAt: value.expiresAt,
			});
			return;
		}
		if (value.type === "node") {
			savePendingDelete({
				type: "node",
				schemeId: value.schemeId,
				procedureId: value.procedureId,
				itemName: value.itemName,
				expiresAt: value.expiresAt,
				deleteRequest: value.deleteRequest,
			});
			return;
		}
		savePendingDelete({
			type: "checklist",
			schemeId: value.schemeId,
			procedureId: value.procedureId,
			itemName: value.itemName,
			expiresAt: value.expiresAt,
		});
	}, []);

	const finalizeDelete = useCallback(
		async (target: PendingDelete) => {
			if (target.type === "custom") {
				await target.onCommit();
				return;
			}
			if (target.type === "procedure") {
				await SATOSPortal.DeleteProcedure({ procedureId: target.procedureId });
				await queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedure, target.procedureId],
				});
				await queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureDetails, target.procedureId],
				});
				await queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedure],
				});
				await queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedures],
				});
				await queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureGroups],
				});
				return;
			}
			if (target.type === "checklist") {
				await deleteChecklist({
					SchemeId: target.schemeId,
					ProcedureId: target.procedureId,
				});
				void queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedure, target.procedureId],
				});
				void queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureDetails, target.procedureId],
				});
				void queryClient.invalidateQueries({
					queryKey: [ChecklistQueryKeys.checklist, target.schemeId],
				});
				return;
			}
			await deleteProcedureNode(target.deleteRequest);
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedureDetails, target.procedureId],
			});
			for (const checklistId of target.deleteRequest.Checklists ?? []) {
				if (checklistId > 0) {
					void queryClient.invalidateQueries({
						queryKey: [ChecklistQueryKeys.checklist, checklistId],
					});
				}
			}
		},
		[deleteChecklist, deleteProcedureNode, queryClient],
	);

	const commitPendingDelete = useCallback(async () => {
		const current = pendingRef.current;
		if (!current) return;
		clearTimer();
		clearPendingDelete();
		setPending(null);
		try {
			await finalizeDelete(current);
		} catch {
			// Deletion failed; restore server state in UI.
			if (current.type !== "custom") {
				void queryClient.invalidateQueries({
					queryKey: [ProcedureQueryKeys.procedureDetails, current.procedureId],
				});
			}
		}
	}, [clearTimer, finalizeDelete, queryClient]);

	const scheduleCommit = useCallback(
		(target: PendingDelete) => {
			clearTimer();
			const remaining = Math.max(0, target.expiresAt - Date.now());
			if (remaining === 0) {
				void commitPendingDelete();
				return;
			}
			timerRef.current = setTimeout(() => {
				void commitPendingDelete();
			}, remaining);
		},
		[clearTimer, commitPendingDelete],
	);

	// Restore pending delete after refresh and resume timer.
	useEffect(() => {
		const restored = parsePendingDelete();
		if (!restored) return;

		if (restored.expiresAt <= Date.now()) {
			clearPendingDelete();
			if (restored.type === "procedure") {
				void SATOSPortal.DeleteProcedure({ procedureId: restored.procedureId });
			} else if (restored.type === "checklist") {
				void deleteChecklist({
					SchemeId: restored.schemeId,
					ProcedureId: restored.procedureId,
				});
			} else {
				void deleteProcedureNode(restored.deleteRequest);
			}
			return;
		}

		const previousProcedureDetails =
			restored.type === "procedure"
				? undefined
				: queryClient.getQueryData<ProcedureDetails>([
						ProcedureQueryKeys.procedureDetails,
						restored.procedureId,
					]);
		const hydrated: PendingDelete =
			restored.type === "procedure"
				? restored
				: {
						...restored,
						previousProcedureDetails,
					};

		setPending(hydrated);
		if (hydrated.type === "procedure") {
			applyOptimisticProcedureRemoval(queryClient, hydrated.procedureId);
		} else {
			applyOptimisticUpdate(
				queryClient,
				hydrated.procedureId,
				hydrated.schemeId,
				previousProcedureDetails,
			);
		}
		scheduleCommit(hydrated);
	}, [deleteChecklist, deleteProcedureNode, queryClient, scheduleCommit]);

	// Keep pending deletions hidden when procedure details refetch while undo is active.
	useEffect(() => {
		if (!pending || pending.type === "procedure" || pending.type === "custom")
			return;
		const unsubscribe = queryClient
			.getQueryCache()
			.subscribe((event: QueryCacheEventShape) => {
				const queryKey = event.query?.queryKey;
				if (!Array.isArray(queryKey)) return;
				if (queryKey[0] !== ProcedureQueryKeys.procedureDetails) return;
				const procedureId =
					typeof queryKey[1] === "number" ? queryKey[1] : Number(queryKey[1]);
				if (
					!Number.isFinite(procedureId) ||
					procedureId !== pending.procedureId
				)
					return;

				const current = queryClient.getQueryData<ProcedureDetails>([
					ProcedureQueryKeys.procedureDetails,
					procedureId,
				]);
				if (!current?.ProcedureDetailNodes?.length) return;
				if (!hasNode(current.ProcedureDetailNodes, pending.schemeId)) return;

				queryClient.setQueryData(
					[ProcedureQueryKeys.procedureDetails, procedureId],
					{
						...current,
						ProcedureDetailNodes: filterOutNode(
							current.ProcedureDetailNodes,
							pending.schemeId,
						),
					},
				);
			});
		return unsubscribe;
	}, [pending, queryClient]);

	// Keep pending procedure deletions hidden when lists refetch while undo is active.
	useEffect(() => {
		if (!pending || pending.type !== "procedure") return;
		applyOptimisticProcedureRemoval(queryClient, pending.procedureId);
		const unsubscribe = queryClient
			.getQueryCache()
			.subscribe((event: QueryCacheEventShape) => {
				const queryKey = event.query?.queryKey;
				if (!Array.isArray(queryKey)) return;
				if (queryKey[0] !== ProcedureQueryKeys.procedures) return;
				const current = queryClient.getQueryData(queryKey);
				if (!containsProcedureInCollection(current, pending.procedureId))
					return;
				queryClient.setQueryData(
					queryKey,
					removeProcedureFromCollection(current, pending.procedureId),
				);
			});
		return unsubscribe;
	}, [pending, queryClient]);

	const showDeleteChecklistUndo = useCallback(
		(checklistId: number, procedureId: number, checklistName: string) => {
			if (pendingRef.current) {
				void commitPendingDelete();
			}
			const previousProcedureDetails =
				queryClient.getQueryData<ProcedureDetails>([
					ProcedureQueryKeys.procedureDetails,
					procedureId,
				]);
			applyOptimisticUpdate(
				queryClient,
				procedureId,
				checklistId,
				previousProcedureDetails,
			);
			navigate(`/procedure/${procedureId}`);
			const nextPending: PendingDelete = {
				type: "checklist",
				schemeId: checklistId,
				procedureId,
				itemName: checklistName,
				expiresAt: Date.now() + UNDO_DURATION_MS,
				previousProcedureDetails,
			};
			setPending(nextPending);
			persistPending(nextPending);
			scheduleCommit(nextPending);
		},
		[
			queryClient,
			navigate,
			persistPending,
			scheduleCommit,
			commitPendingDelete,
		],
	);

	const showDeleteNodeUndo = useCallback(
		(
			schemeId: number,
			procedureId: number,
			nodeName: string,
			deleteRequest: DeleteProcedureNodeRequest,
			options?: { onClose?: () => void },
		) => {
			if (pendingRef.current) {
				void commitPendingDelete();
			}
			const previousProcedureDetails =
				queryClient.getQueryData<ProcedureDetails>([
					ProcedureQueryKeys.procedureDetails,
					procedureId,
				]);
			applyOptimisticUpdate(
				queryClient,
				procedureId,
				schemeId,
				previousProcedureDetails,
			);
			options?.onClose?.();
			const nextPending: PendingDelete = {
				type: "node",
				schemeId,
				procedureId,
				itemName: nodeName,
				expiresAt: Date.now() + UNDO_DURATION_MS,
				previousProcedureDetails,
				deleteRequest,
			};
			setPending(nextPending);
			persistPending(nextPending);
			scheduleCommit(nextPending);
		},
		[queryClient, persistPending, scheduleCommit, commitPendingDelete],
	);

	const showDeleteProcedureUndo = useCallback(
		(procedureId: number, procedureName: string) => {
			if (pendingRef.current) {
				void commitPendingDelete();
			}
			applyOptimisticProcedureRemoval(queryClient, procedureId);
			navigate("/procedures");
			const nextPending: PendingDelete = {
				type: "procedure",
				procedureId,
				itemName: procedureName,
				expiresAt: Date.now() + UNDO_DURATION_MS,
			};
			setPending(nextPending);
			persistPending(nextPending);
			scheduleCommit(nextPending);
		},
		[
			queryClient,
			navigate,
			persistPending,
			scheduleCommit,
			commitPendingDelete,
		],
	);

	const showDeferredUndo = useCallback(
		(options: {
			message: string;
			onCommit: () => Promise<void> | void;
			onUndo?: () => void;
			durationMs?: number;
		}) => {
			if (pendingRef.current) {
				void commitPendingDelete();
			}
			const nextPending: PendingDelete = {
				type: "custom",
				id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
				message: options.message,
				expiresAt: Date.now() + (options.durationMs ?? UNDO_DURATION_MS),
				onCommit: options.onCommit,
				onUndo: options.onUndo,
			};
			setPending(nextPending);
			scheduleCommit(nextPending);
		},
		[commitPendingDelete, scheduleCommit],
	);

	const contextValue: ContextValue = React.useMemo(
		() => ({
			showDeleteChecklistUndo,
			showDeleteNodeUndo,
			showDeleteProcedureUndo,
			showDeferredUndo,
		}),
		[
			showDeleteChecklistUndo,
			showDeleteNodeUndo,
			showDeleteProcedureUndo,
			showDeferredUndo,
		],
	);

	const handleClose = useCallback(
		(_: React.SyntheticEvent | Event, reason?: string) => {
			if (reason === "clickaway") return;
			void commitPendingDelete();
		},
		[commitPendingDelete],
	);

	const handleUndo = useCallback(() => {
		const current = pendingRef.current;
		if (!current) return;
		clearTimer();
		clearPendingDelete();
		setPending(null);
		if (current.type === "custom") {
			current.onUndo?.();
			return;
		}
		if (current.type !== "procedure" && current.previousProcedureDetails) {
			queryClient.setQueryData(
				[ProcedureQueryKeys.procedureDetails, current.procedureId],
				current.previousProcedureDetails,
			);
		}
		void queryClient.invalidateQueries({
			queryKey: [ProcedureQueryKeys.procedureDetails, current.procedureId],
		});
		if (current.type === "procedure") {
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedure, current.procedureId],
			});
			void queryClient.invalidateQueries({
				queryKey: [ProcedureQueryKeys.procedures],
			});
		} else if (current.type === "checklist") {
			void queryClient.invalidateQueries({
				queryKey: [ChecklistQueryKeys.checklist, current.schemeId],
			});
		}
	}, [clearTimer, queryClient]);

	const handleDeleteNow = useCallback(() => {
		void commitPendingDelete();
	}, [commitPendingDelete]);

	const snackbarMessage =
		pending?.type === "checklist" ? (
			<Translate String="Checklist.Deleted.Undo">
				Checklist deleted - Undo (30s)
			</Translate>
		) : pending?.type === "procedure" ? (
			<Translate String="Procedure.Deleted.Undo">
				Procedure deleted - Undo (30s)
			</Translate>
		) : pending?.type === "custom" ? (
			pending.message
		) : (
			<Translate String="Node.Deleted.Undo">
				Node deleted - Undo (30s)
			</Translate>
		);
	const snackbarCountdownMs = pending
		? Math.max(0, pending.expiresAt - Date.now())
		: 0;
	const snackbarKey = pending
		? pending.type === "custom"
			? `${pending.type}-${pending.id}-${pending.expiresAt}`
			: `${pending.type}-${pending.type === "procedure" ? pending.procedureId : pending.schemeId}-${pending.expiresAt}`
		: "no-pending-delete";

	return (
		<DestructiveActionUndoContext.Provider value={contextValue}>
			{children}
			<Snackbar
				open={!!pending}
				autoHideDuration={null}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				sx={{ mb: 2 }}
			>
				<Alert
					severity="success"
					variant="standard"
					onClose={handleClose}
					action={
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Button
								color="success"
								variant="contained"
								size="small"
								onClick={handleUndo}
								sx={(theme) => ({
									fontWeight: 700,
									textTransform: "none",
									minWidth: 84,
									borderRadius: 1.5,
									boxShadow: "none",
									color: theme.palette.success.contrastText,
									backgroundColor: theme.palette.success.main,
									"&:hover": {
										backgroundColor: theme.palette.success.dark,
										boxShadow: "none",
									},
								})}
							>
								<Translate String="Common.Undo">Undo</Translate>
							</Button>
							<Button
								color="error"
								variant="outlined"
								size="small"
								onClick={handleDeleteNow}
								sx={(theme) => ({
									fontWeight: 700,
									textTransform: "none",
									minWidth: 84,
									borderRadius: 1.5,
									boxShadow: "none",
									borderColor: theme.palette.error.main,
									"&:hover": {
										borderColor: theme.palette.error.dark,
										backgroundColor: alpha(theme.palette.error.main, 0.08),
										boxShadow: "none",
									},
								})}
							>
								<Translate String="Common.Delete">Delete</Translate>
							</Button>
						</Box>
					}
					sx={(theme) => ({
						width: "100%",
						maxWidth: { xs: "calc(100vw - 24px)", sm: 520 },
						"& .MuiAlert-action": {
							display: "flex",
							alignItems: "center",
							paddingLeft: 1.5,
							marginRight: 0,
						},
						"& .MuiAlert-message": {
							width: "100%",
							padding: "6px 0",
							color: theme.palette.text.primary,
						},
					})}
				>
					<Box>
						<Typography
							variant="body2"
							sx={{ fontWeight: 600, lineHeight: 1.35 }}
						>
							{snackbarMessage}
						</Typography>
						<Box
							sx={(theme) => ({
								marginTop: 0.75,
								height: 5,
								borderRadius: 999,
								backgroundColor: alpha(theme.palette.success.main, 0.22),
								overflow: "hidden",
							})}
						>
							<Box
								key={snackbarKey}
								sx={(theme) => ({
									height: "100%",
									width: "100%",
									backgroundColor: theme.palette.success.main,
									transformOrigin: "left center",
									animation:
										snackbarCountdownMs > 0
											? `undoSnackbarCountdown ${snackbarCountdownMs}ms linear forwards`
											: "none",
									"@keyframes undoSnackbarCountdown": {
										from: { transform: "scaleX(1)" },
										to: { transform: "scaleX(0)" },
									},
								})}
							/>
						</Box>
					</Box>
				</Alert>
			</Snackbar>
		</DestructiveActionUndoContext.Provider>
	);
}

export function useDestructiveActionUndo() {
	const ctx = useContext(DestructiveActionUndoContext);
	if (!ctx) {
		// Fallback to immediate destructive actions when provider scope is missing.
		// This avoids hard crashes in legacy routes while preserving core behavior.
		if (!hasWarnedMissingUndoProvider) {
			console.error(
				"useDestructiveActionUndo used outside provider; falling back to immediate actions without undo.",
			);
			hasWarnedMissingUndoProvider = true;
		}
		return {
			showDeleteChecklistUndo: (checklistId, procedureId) => {
				void SATOSPortal.DeleteProcedureChecklist({
					ProcedureId: procedureId,
					SchemeId: checklistId,
				}).catch((error: unknown) => {
					console.error(error);
				});
			},
			showDeleteNodeUndo: (
				_schemeId,
				_procedureId,
				_nodeName,
				deleteRequest,
				options,
			) => {
				void SATOSPortal.DeleteProcedureNode(deleteRequest)
					.then(() => {
						options?.onClose?.();
					})
					.catch((error: unknown) => {
						console.error(error);
					});
			},
			showDeleteProcedureUndo: (procedureId) => {
				void SATOSPortal.DeleteProcedure({ procedureId }).catch(
					(error: unknown) => {
						console.error(error);
					},
				);
			},
			showDeferredUndo: ({ onCommit }) => {
				void Promise.resolve(onCommit()).catch((error: unknown) => {
					console.error(error);
				});
			},
		} satisfies ContextValue;
	}
	return ctx;
}

/** @deprecated Use useDestructiveActionUndo().showDeleteChecklistUndo instead */
export function useDeleteChecklistUndo() {
	const ctx = useDestructiveActionUndo();
	return { showDeleteUndoSnackbar: ctx.showDeleteChecklistUndo };
}
